// Copyright (c) 2026. LodgIQ Inc, All rights reserved
//
// AL-86 — Pickup vs rate (the dashboard's Sensitivity Analysis, as an answer).
// Everything on this card is MEASURED: how the book built by days-before-arrival
// vs a DOW-aligned comparison window, the sell rate alongside, the booked-ADR-
// vs-BAR gap, and the rate moves in the window (push audit). Chips re-query the
// token-free /sensitivity/ endpoint; nothing here costs tokens after the first
// answer. The "why" deliberately lives elsewhere: the interpret button routes to
// diagnose_revenue_gap rather than letting this card speculate.

import React, { useMemo, useState } from "react";
import { runSensitivity } from "../../services/wizard.service";

const W = 520, H = 200, PAD_L = 46, PAD_B = 32, PAD_T = 14;

function moneyFmt(currency) {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: currency || "USD", maximumFractionDigits: 0 });
  } catch (_) {
    return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });
  }
}

export default function PickupSensitivityCard({ widget, propertyId, onInterpret }) {
  const money = moneyFmt(widget && widget.currency);
  const [view, setView] = useState(widget || {});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [weekends, setWeekends] = useState(false);

  const reload = async (opts) => {
    if (!propertyId) return;
    setBusy(true);
    setErr("");
    try {
      const w = view.windows || {};
      const res = await runSensitivity(propertyId, {
        from: (w.primary || {}).from, to: (w.primary || {}).to, ...opts,
      });
      setView(res);
      if (opts && "weekendsOnly" in opts) setWeekends(!!opts.weekendsOnly);
    } catch (e) {
      setErr((e && e.message) || "Couldn't load that view.");
    } finally {
      setBusy(false);
    }
  };

  const { pts, maxRooms, maxDba } = useMemo(() => {
    const rows = (view.rows || []).filter((r) => r.dba >= 0);
    const vals = rows.flatMap((r) => [r.rooms, r.comp_rooms]).filter((v) => v != null);
    // today's own room count belongs in the scale — without it the marker can
    // float above the axis maximum (seen live: 705 rn above a 674 axis)
    if (view.current && view.current.rooms != null) vals.push(view.current.rooms);
    if (view.current && view.current.comp_final_rooms != null) vals.push(view.current.comp_final_rooms);
    return { pts: rows, maxRooms: Math.max(1, ...vals), maxDba: Math.max(1, ...rows.map((r) => r.dba)) };
  }, [view]);

  if (!pts.length) {
    return <div className="wiz-calc wiz-calc--fallback">No sensitivity data for this period.</div>;
  }
  if (widget && widget.schema_version && widget.schema_version !== 1) {
    return <div className="wiz-calc wiz-calc--fallback">This card needs a newer app version. Please refresh.</div>;
  }

  // x: dba maxDba..0 left->right (approaching arrival); y: rooms
  const x = (dba) => PAD_L + ((maxDba - dba) / maxDba) * (W - PAD_L - 10);
  const y = (rooms) => PAD_T + (1 - rooms / maxRooms) * (H - PAD_T - PAD_B);
  const path = (key) => {
    const p = pts.filter((r) => r[key] != null);
    return p.length ? "M" + p.map((r) => `${x(r.dba).toFixed(1)},${y(r[key]).toFixed(1)}`).join(" L") : "";
  };

  const wins = view.windows || {};
  const cur = view.current;
  const gap = view.adr_vs_bar;
  const moves = view.rate_moves;
  const single = view.mode === "single_date";
  const paceBehind = cur && cur.pace_gap_rooms != null && cur.pace_gap_rooms < 0;

  const interpret = () => onInterpret && onInterpret(
    paceBehind
      ? `Why is ${(wins.primary || {}).from} to ${(wins.primary || {}).to} building behind the comparison period and what are my options?`
      : `Interpret the pickup-vs-rate picture for ${(wins.primary || {}).from} to ${(wins.primary || {}).to}.`
  );

  return (
    <div className="wiz-calc wiz-sens">
      <div className="wiz-calc__head">
        <span className="wiz-calc__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 12h4l2-7 4 14 2-7h6" />
          </svg>
        </span>
        <span className="wiz-calc__title">Pickup vs rate</span>
        <span className="wiz-sim__cp-badge wiz-sens__measured">measured</span>
        <span className="wiz-sim__date">{(wins.primary || {}).from} → {(wins.primary || {}).to}</span>
      </div>

      <div className="wiz-sens__sub">
        vs {(wins.comparison || {}).from} → {(wins.comparison || {}).to}
        {(wins.comparison || {}).basis === "same days of week" ? " (same days of week, last year)" : ""}
      </div>

      {!single && (
        <div className="wiz-sens__chips">
          <button disabled={busy} className={!weekends ? "is-on" : ""}
            onClick={() => reload({ weekendsOnly: false })}>all days</button>
          <button disabled={busy} className={weekends ? "is-on" : ""}
            onClick={() => reload({ weekendsOnly: true })}>weekends</button>
          {busy && <span className="wiz-cal__busy">loading…</span>}
        </div>
      )}
      {err && <div className="wiz-sim__pferr">{err}</div>}

      <svg viewBox={`0 0 ${W} ${H}`} className="wiz-sens__chart" role="img"
           aria-label="Rooms on the books by days before arrival, this period versus the comparison">
        <line x1={PAD_L} y1={H - PAD_B} x2={W - 8} y2={H - PAD_B} className="wiz-sens__axis" />
        <line x1={PAD_L} y1={PAD_T} x2={PAD_L} y2={H - PAD_B} className="wiz-sens__axis" />
        {pts.map((r) => (
          <text key={`t${r.dba}`} x={x(r.dba)} y={H - PAD_B + 14} textAnchor="middle" className="wiz-sens__tick">{r.dba}</text>
        ))}
        <text x={(PAD_L + W) / 2} y={H - 4} textAnchor="middle" className="wiz-sens__tick">days before arrival</text>
        <text x={PAD_L - 6} y={PAD_T + 8} textAnchor="end" className="wiz-sens__tick">{maxRooms.toLocaleString()}</text>
        <text x={PAD_L - 6} y={H - PAD_B} textAnchor="end" className="wiz-sens__tick">0</text>
        <path d={path("comp_rooms")} className="wiz-sens__line wiz-sens__line--comp" />
        <path d={path("rooms")} className="wiz-sens__line wiz-sens__line--ty" />
        {pts.map((r) => {
          const bits = [];
          if (r.rooms != null) bits.push(`${r.rooms.toLocaleString()} rn`);
          if (r.adr != null) bits.push(`booked ADR ${money.format(r.adr)}`);
          if (r.sell_rate != null) bits.push(`sell rate ${money.format(r.sell_rate)}`);
          if (r.comp_rooms != null) bits.push(`comparison ${r.comp_rooms.toLocaleString()} rn`);
          const tip = `${r.dba} days out · ${bits.join(" · ")}`;
          return (
            <g key={`pt${r.dba}`}>
              {r.comp_rooms != null && (
                <circle cx={x(r.dba)} cy={y(r.comp_rooms)} r="3" className="wiz-sens__pt wiz-sens__pt--comp">
                  <title>{tip}</title>
                </circle>
              )}
              {r.rooms != null && (
                <circle cx={x(r.dba)} cy={y(r.rooms)} r="3.5" className="wiz-sens__pt wiz-sens__pt--ty">
                  <title>{tip}</title>
                </circle>
              )}
              {/* generous invisible hit area — 3px dots are hard to hover */}
              <rect x={x(r.dba) - 12} y={PAD_T} width="24" height={H - PAD_T - PAD_B}
                    fill="transparent">
                <title>{tip}</title>
              </rect>
            </g>
          );
        })}
        {cur && cur.dba != null && (
          <g>
            <line x1={x(cur.dba)} y1={PAD_T} x2={x(cur.dba)} y2={H - PAD_B} className="wiz-sens__today" />
            <circle cx={x(cur.dba)} cy={y(cur.rooms)} r="4.5" className="wiz-sens__dot" />
            <text x={x(cur.dba) + 6} y={PAD_T + 10} className="wiz-sens__note">
              today · {cur.rooms.toLocaleString()} rn{cur.comp_rooms_same_dba != null ? ` (LY here: ${cur.comp_rooms_same_dba.toLocaleString()})` : ""}
            </text>
          </g>
        )}
      </svg>

      <div className="wiz-sens__legend">
        <span><i className="wiz-sens__swatch wiz-sens__swatch--ty" />this period</span>
        <span><i className="wiz-sens__swatch wiz-sens__swatch--comp" />comparison{cur && cur.comp_final_rooms != null ? ` (finished at ${cur.comp_final_rooms.toLocaleString()})` : ""}</span>
        {cur && cur.pace_gap_rooms != null && (
          <span className={`wiz-sens__pace ${paceBehind ? "is-behind" : "is-ahead"}`}>
            {paceBehind ? "" : "+"}{cur.pace_gap_rooms.toLocaleString()} rn vs comparison pace
          </span>
        )}
      </div>

      <div className="wiz-sens__tiles">
        {gap && (
          <div className="wiz-sens__tile">
            <div className="wiz-sens__tile-label">Booked ADR vs sell rate</div>
            <div className="wiz-sens__tile-val">{money.format(gap.booked_adr)} vs {money.format(gap.sell_rate)}</div>
            <div className="wiz-sens__tile-note">
              {gap.reading || `bookings land ${money.format(Math.abs(gap.gap))} ${gap.gap >= 0 ? "under" : "over"} BAR`}
            </div>
          </div>
        )}
        {moves && (
          <div className="wiz-sens__tile">
            <div className="wiz-sens__tile-label">Rate moves in this window</div>
            <div className="wiz-sens__tile-val">{moves.pushes_in_window} push{moves.pushes_in_window === 1 ? "" : "es"}</div>
            <div className="wiz-sens__tile-note">
              {moves.latest ? `latest: ${money.format(moves.latest.pushed_rate)} · ${moves.latest.pushed_by} · ${String(moves.latest.pushed_at || "").slice(0, 10)}` : ""}
              {moves.pickup_before_per_day != null && moves.pickup_after_per_day != null
                ? ` — pickup ${moves.pickup_before_per_day}/day before, ${moves.pickup_after_per_day}/day after`
                : ""}
            </div>
          </div>
        )}
      </div>
      {moves && moves.speed_note && <div className="wiz-sens__caveat">{moves.speed_note}.</div>}

      {onInterpret && (
        <button className="wiz-calc__interpret" onClick={interpret}>
          {paceBehind ? "Diagnose the gap ↗" : "Ask LodgIQ to interpret ↗"}
        </button>
      )}
      <div className="wiz-calc__foot">© {new Date().getFullYear()} LodgIQ</div>
    </div>
  );
}
